import React, { useMemo, useState } from 'react';
import { Box, Divider, IconButton, Typography, Button, Paper, Popover } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CheckboxGroup from './CheckboxGroup';
import SearchableSelectDropdown from './SearchableSelectDropdown';
import { FilterConfigSection, FilterValues } from './type';

type OptionType = {
  label: string;
  value: string;
};

type Props = {
  sections: FilterConfigSection[];

  /**
   * Optional initial filters.
   * This replaces AdvancedFiltersCard initialFilters.
   */
  defaultValues?: FilterValues;
  
// Popover control
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;

// Apply callback
  onApply: (values: FilterValues, meta: { isAnyFilterApplied: boolean }) => void;

  popupWidth?: number;
  maxHeight?: number;
};

const normalizeOptions = (options: any[] = []): OptionType[] => {
  return options.map((option) =>
    typeof option === 'string'
      ? { label: option, value: option }
      : option
  );
};

const createInitial = (
  sections: FilterConfigSection[],
  defaults?: FilterValues
): FilterValues => {
  const init: FilterValues = {};

  sections.forEach((section) => {
    section.items.forEach((item) => {
      if (defaults && item.key in defaults) {
        init[item.key] = defaults[item.key]!;
      } else if (item.type === 'checkbox_group' || item.type === 'dropdown_multi') {
        init[item.key] = [];
      } else {
        init[item.key] = null;
      }
    });
  });

  return init;
};

/**
 * Main logic:
 *
 * 1. If options have explicit value "all":
 *    - pass ["all"]
 *
 * 2. If options do not have value "all":
 *    - pass all actual option values
 *
 * Example:
 * Business Unit options have { value: "all" }
 * output: { "Business Unit": ["all"] }
 *
 * Sector options do not have { value: "all" }
 * output: { Sector: ["pbna", "pfna", "amesa", "apac"] }
 */
const normalizeValuesForApply = (
  sections: FilterConfigSection[],
  values: FilterValues
): FilterValues => {
  const normalizedValues: FilterValues = { ...values };

  sections.forEach((section) => {
    section.items.forEach((item) => {
      if (item.type !== 'checkbox_group' && item.type !== 'dropdown_multi') return;

      const currentValue = values[item.key];

      if (!Array.isArray(currentValue)) {
        normalizedValues[item.key] = [];
        return;
      }

      if (currentValue.length === 0) {
        normalizedValues[item.key] = [];
        return;
      }

      const options = normalizeOptions(item.options);
      const allOption = options.find((option) => option.value === 'all');
      const optionsWithoutAll = options.filter((option) => option.value !== 'all');
      const optionValuesWithoutAll = optionsWithoutAll.map((option) => option.value);

      const hasSelectedAll = currentValue.includes('all');

      const hasSelectedEveryItem =
        optionValuesWithoutAll.length > 0 &&
        optionValuesWithoutAll.every((value) => currentValue.includes(value));

      /**
       * Case 1:
       * If actual "all" option exists, pass only ["all"].
       */
      if (allOption && (hasSelectedAll || hasSelectedEveryItem)) {
        normalizedValues[item.key] = ['all'];
        return;
      }

      /**
       * Case 2:
       * If actual "all" option does not exist, but UI selected all,
       * pass all real option values.
       */
      if (!allOption && (hasSelectedAll || hasSelectedEveryItem)) {
        normalizedValues[item.key] = optionValuesWithoutAll;
        return;
      }

      /**
       * Case 3:
       * Partial selection.
       */
      normalizedValues[item.key] = currentValue.filter((value) => value !== 'all');
    });
  });

  return normalizedValues;
};

export default function MultiFilterPanelPopup({
  sections,
  defaultValues,
  anchorEl,
  open,
  onClose,
  onApply,
  popupWidth = 340,
  maxHeight = 520,
}: Props) {
  const theme = useTheme();
  const colors = theme.palette;

  const [values, setValues] = useState<FilterValues>(() =>
    createInitial(sections, defaultValues)
  );

  React.useEffect(() => {
    if (!open) return;
    setValues(createInitial(sections, defaultValues));
  }, [open, sections, defaultValues]);

  const setKey = (key: string, next: any) => {
    setValues((previous) => ({
      ...previous,
      [key]: next,
    }));
  };

  const normalizedApplyValues = useMemo(() => {
    return normalizeValuesForApply(sections, values);
  }, [sections, values]);

  const isAnyFilterApplied = useMemo(() => {
    return Object.values(normalizedApplyValues).some((value) => {
      if (value == null) return false;
      if (Array.isArray(value)) return value.length > 0;
      return String(value).trim().length > 0;
    });
  }, [normalizedApplyValues]);

  const handleClear = () => {
    setValues(createInitial(sections));
  };

  const handleApply = () => {
    onApply(normalizedApplyValues, { isAnyFilterApplied });
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: {
            '&&': {
              m: 0,
              margin: '0 !important',
              p: 0,
              padding: '0 !important',
            },
            width: popupWidth,
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: colors.filterPanel.panelBackground,
            boxShadow: colors.filterPanel.panelShadow,
            border: `1px solid ${colors.filterPanel.panelBorder}`,
          },
        },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          '&&': {
            m: 0,
            margin: '0 !important',
            p: 0,
            padding: '0 !important',
          },
          bgcolor: `${colors.filterPanel.panelBackground} !important`,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 500,
              color: colors.filterPanel.headerTitle,
            }}
          >
            Filters
          </Typography>

          <IconButton size="small" onClick={onClose} sx={{ '&&': { p: 0.5 } }}>
            <CloseIcon fontSize="small" sx={{ color: colors.filterPanel.headerTitle }} />
          </IconButton>
        </Box>

        <Divider />

        <Box
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            overflowY: 'auto',
            maxHeight,
          }}
        >
          {sections.map((section, sectionIndex) => (
            <Box
              key={sectionIndex}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {section.title && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: colors.filterPanel.sectionTitle,
                    }}
                  >
                    {section.title}
                  </Typography>
                  <Divider />
                </Box>
              )}

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                {section.items.map((item) => {
                  const value = values[item.key];

                  if (item.type === 'checkbox_group') {
                    return (
                      <CheckboxGroup
                        key={item.key}
                        label={item.label}
                        options={item.options}
                        values={Array.isArray(value) ? value : []}
                        onChange={(next) => setKey(item.key, next)}
                      />
                    );
                  }

                  if (item.type === 'dropdown_single') {
                    return (
                      <SearchableSelectDropdown
                        key={item.key}
                        label={item.label}
                        placeholder={item.placeholder ?? `All ${item.label}`}
                        options={item.options}
                        multiple={false}
                        value={typeof value === 'string' ? value : null}
                        onChange={(next) => setKey(item.key, next)}
                        showSearch={item.showSearch}
                        showAllOption={item.showAllOption}
                        chipDisplay={false}
                        showFieldBackground
                      />
                    );
                  }

                  return (
                    <SearchableSelectDropdown
                      key={item.key}
                      label={item.label}
                      placeholder={item.placeholder ?? `All ${item.label}`}
                      options={item.options}
                      multiple
                      value={Array.isArray(value) ? value : []}
                      onChange={(next) => setKey(item.key, next)}
                      showSearch={item.showSearch}
                      showAllOption={item.showAllOption}
                      chipDisplay={item.chipDisplay ?? true}
                      showFieldBackground
                    />
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            p: 2,
            gap: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            bgcolor: colors.filterPanel.footerBackground,
          }}
        >
          <Button
            variant="text"
            onClick={handleClear}
            sx={{
              '&&': {
                color: colors.filterPanel.clearButtonText,
                fontWeight: 500,
                textTransform: 'uppercase',
                fontSize: 14,
                p: 0,
                minWidth: 'unset',
              },
            }}
          >
            Clear Filters
          </Button>

          <Button
            variant="contained"
            onClick={handleApply}
            sx={{
              '&&': {
                bgcolor: colors.filterPanel.applyButtonBackground,
                '&:hover': {
                  bgcolor: colors.filterPanel.applyButtonHoverBackground,
                },
                fontWeight: 500,
                fontSize: 14,
                textTransform: 'uppercase',
                borderRadius: '4px',
                px: 3,
                py: 1,
                minHeight: 36,
              },
            }}
          >
            Apply
          </Button>
        </Box>
      </Paper>
    </Popover>
  );
}